import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'

// ─── Types ────────────────────────────────────────────────────

export interface NoizaGmv {
  total_gmv: number
  total_orders: number
  avg_ticket: number
}

export interface NoizaGmvDaily {
  day: string
  gmv: number
  orders: number
}

export interface NoizaGmvByRestaurant {
  restaurant_id: string
  restaurant_name: string
  slug: string
  gmv: number
  orders: number
  avg_ticket: number
}

export interface NoizaChannelDistribution {
  channel: string
  orders: number
  pct: number
}

export interface NoizaSelfServicePct {
  /** Marketplace (web) + QR, sobre TODAS las órdenes válidas. */
  self_service_pct: number
  /** Mesero (source = 'waiter'), sobre TODAS las órdenes válidas. */
  waiter_pct: number
  /** Caja (source = 'pos_quick_sale'), sobre TODAS las órdenes válidas. */
  cashier_pct: number
  /** Sin clasificar / otros orígenes, sobre TODAS las órdenes válidas. */
  unclassified_pct: number
}

export interface NoizaRepeatCustomers {
  repeat_count: number
  repeat_pct: number
}

export interface NoizaTopProduct {
  product_name: string
  restaurant_name: string
  restaurant_id?: string
  quantity: number
  revenue: number
}

export interface NoizaLowDay {
  restaurant_id: string
  restaurant_name: string
  day_of_week: string | number
  avg_gmv: number
  avg_orders: number
}

export interface NoizaLowHour {
  restaurant_id: string
  restaurant_name: string
  hour_of_day: string | number
  avg_gmv: number
  avg_orders: number
}

export interface NoizaSalesDaily {
  restaurant_id: string
  restaurant_name: string
  day: string
  gmv: number
  orders: number
}

export interface NoizaSalesHourly {
  restaurant_id: string
  restaurant_name: string
  hour: string | number
  gmv: number
  orders: number
}

export interface NoizaOrderValid {
  id: string
  created_at: string
  total: number
  status: string
  restaurant_id: string
  restaurant_name?: string
  customer_name?: string
}

export interface NoizaCollectedByRestaurant {
  restaurant_id: string
  restaurant_name: string
  amount: number
  payments: number
}

export interface NoizaCollected {
  /** Suma de payments.amount tal como está registrada (CRC). */
  total: number
  payments: number
  byRestaurant: NoizaCollectedByRestaurant[]
}

export interface NoizaMetricsData {
  gmv: NoizaGmv
  gmvDaily: NoizaGmvDaily[]
  gmvByRestaurant: NoizaGmvByRestaurant[]
  channelDistribution: NoizaChannelDistribution[]
  selfServicePct: NoizaSelfServicePct
  activeRestaurants: number
  uniqueCustomers: number
  repeatCustomers: NoizaRepeatCustomers
  topProducts: NoizaTopProduct[]
  lowDays: NoizaLowDay[]
  lowHours: NoizaLowHour[]
  salesDaily: NoizaSalesDaily[]
  salesHourly: NoizaSalesHourly[]
  recentOrders: NoizaOrderValid[]
  collected: NoizaCollected
}

const DEFAULT_DATA: NoizaMetricsData = {
  gmv: { total_gmv: 0, total_orders: 0, avg_ticket: 0 },
  gmvDaily: [],
  gmvByRestaurant: [],
  channelDistribution: [],
  selfServicePct: {
    self_service_pct: 0,
    waiter_pct: 0,
    cashier_pct: 0,
    unclassified_pct: 0,
  },
  activeRestaurants: 0,
  uniqueCustomers: 0,
  repeatCustomers: { repeat_count: 0, repeat_pct: 0 },
  topProducts: [],
  lowDays: [],
  lowHours: [],
  salesDaily: [],
  salesHourly: [],
  recentOrders: [],
  collected: { total: 0, payments: 0, byRestaurant: [] },
}

export interface RestaurantOption {
  id: string
  name: string
  slug: string
}

export interface DateRange {
  from: string
  to: string
}

// Status values that should be EXCLUDED from metrics (cancelled / refunded)
const EXCLUDED_STATUSES = new Set([
  'cancelled',
  'canceled',
  'cancelado',
  'refunded',
  'reembolsado',
  'voided',
])

// ─── Canales ──────────────────────────────────────────────────
// `orders.source` es el campo de origen que escribe cada flujo:
//   web            → checkout del marketplace
//   qr             → menú por QR de mesa
//   waiter         → POS de mesero
//   pos_quick_sale → venta rápida de caja
// Cualquier otro valor (o vacío) queda como "Sin clasificar": nunca se
// convierte automáticamente en Web.
export const CHANNEL_LABELS = {
  web: 'Marketplace',
  qr: 'QR',
  waiter: 'Mesero',
  pos_quick_sale: 'Caja',
  unclassified: 'Sin clasificar',
} as const

type ChannelKey = keyof typeof CHANNEL_LABELS

function classifySource(source: unknown): ChannelKey {
  const s = String(source ?? '')
    .trim()
    .toLowerCase()
  if (s === 'web' || s === 'qr' || s === 'waiter' || s === 'pos_quick_sale') {
    return s
  }
  return 'unclassified'
}

// ─── Zona horaria de Costa Rica ───────────────────────────────
// America/Costa_Rica es UTC-6 todo el año (sin horario de verano), así
// que los límites de rango usan -06:00. La agrupación diaria/horaria usa
// Intl con la zona explícita para no depender del reloj del navegador.
const CR_OFFSET = '-06:00'
const CR_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Costa_Rica',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hourCycle: 'h23',
})

function crDayHour(iso: string): { day: string; hour: number } | null {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  const parts = CR_FORMATTER.formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value || ''
  return {
    day: `${get('year')}-${get('month')}-${get('day')}`,
    hour: Number(get('hour')),
  }
}

function nextDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + 1))
  return dt.toISOString().substring(0, 10)
}

// ─── Paginación completa ──────────────────────────────────────
// La API devuelve como máximo N filas por respuesta (max-rows), sin
// importar el `.limit()` solicitado. Pedimos páginas consecutivas con
// orden estable y avanzamos por la cantidad REAL recibida hasta que una
// página llegue vacía — así funciona aunque el servidor tenga un tope
// menor que PAGE_SIZE. Cualquier error aborta todo: nunca se devuelve
// un resultado parcial.
const PAGE_SIZE = 1000

async function fetchAllPages<T extends { id: unknown }>(
  label: string,
  buildPage: (from: number, to: number) => PromiseLike<{
    data: T[] | null
    error: { message: string } | null
  }>,
): Promise<T[]> {
  const out: T[] = []
  const seen = new Set<string>()
  let offset = 0
  // Tope de seguridad contra bucles infinitos (no es un límite de datos).
  for (let guard = 0; guard < 100000; guard++) {
    const { data, error } = await buildPage(offset, offset + PAGE_SIZE - 1)
    if (error) {
      throw new Error(
        `No se pudieron cargar todos los datos (${label}): ${error.message}`,
      )
    }
    const rows = data || []
    if (rows.length === 0) return out
    for (const r of rows) {
      const key = String(r.id)
      if (seen.has(key)) continue
      seen.add(key)
      out.push(r)
    }
    offset += rows.length
  }
  throw new Error(`Paginación sin fin detectada (${label}).`)
}

async function runInBatches<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = []
  for (let i = 0; i < items.length; i += concurrency) {
    const batch = items.slice(i, i + concurrency)
    results.push(...(await Promise.all(batch.map(fn))))
  }
  return results
}

// ─── Main Hook ─────────────────────────────────────────────

export function useNoizaMetrics(restaurantId?: string, dateRange?: DateRange) {
  const [data, setData] = useState<NoizaMetricsData>(DEFAULT_DATA)
  // Selector: solo restaurantes activos (igual que antes).
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([])
  // Nombres: TODOS los restaurantes, para que el historial de locales
  // inactivos se muestre con su nombre en "Todo".
  const [allRestaurants, setAllRestaurants] = useState<RestaurantOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)

  // Fetch restaurants list once
  useEffect(() => {
    async function fetchRestaurants() {
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('id, name, slug, is_active')
          .order('name')
        if (!error && data) {
          setAllRestaurants(data)
          setRestaurants(data.filter((r: any) => r.is_active === true))
        }
      } catch (err) {
        console.error('Error fetching restaurants:', err)
      }
    }
    fetchRestaurants()
  }, [])

  const fetchAll = useCallback(async () => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(null)

    try {
      const filterById = !!(restaurantId && restaurantId !== 'all')
      const hasDateFilter = !!(dateRange && dateRange.from && dateRange.to)

      // Inicio inclusivo (00:00 CR del día "desde") y fin EXCLUSIVO
      // (00:00 CR del día siguiente a "hasta").
      const fromIso = hasDateFilter
        ? `${dateRange!.from}T00:00:00${CR_OFFSET}`
        : null
      const toIsoExclusive = hasDateFilter
        ? `${nextDay(dateRange!.to)}T00:00:00${CR_OFFSET}`
        : null

      // ─── 1. Orders (por fecha de creación) ─────────────────────
      const ordersRaw = await fetchAllPages<any>('pedidos', (from, to) => {
        let q = supabase
          .from('orders')
          .select(
            'id, created_at, total, status, restaurant_id, source, customer_name, customer_phone',
          )
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to)
        if (hasDateFilter) {
          q = q.gte('created_at', fromIso!).lt('created_at', toIsoExclusive!)
        }
        if (filterById) q = q.eq('restaurant_id', restaurantId)
        return q
      })

      // ─── 2. Payments (por fecha del pago) ──────────────────────
      // No se descartan pagos por el estado actual del pedido: un cobro
      // registrado es dinero que entró. Se suma payments.amount tal cual.
      const paymentsRaw = await fetchAllPages<any>('pagos', (from, to) => {
        let q = supabase
          .from('payments')
          .select('id, amount, created_at, restaurant_id')
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to)
        if (hasDateFilter) {
          q = q.gte('created_at', fromIso!).lt('created_at', toIsoExclusive!)
        }
        if (filterById) q = q.eq('restaurant_id', restaurantId)
        return q
      })

      // Drop cancelled / refunded
      const validOrders = ordersRaw.filter((o: any) => {
        const s = String(o.status || '')
          .toLowerCase()
          .trim()
        return !EXCLUDED_STATUSES.has(s)
      })

      // ─── 3. order_items de las órdenes válidas ─────────────────
      // Por bloques de IDs y, dentro de cada bloque, paginado completo.
      const orderIds = validOrders.map((o: any) => String(o.id))
      const ID_CHUNK = 200
      const idChunks: string[][] = []
      for (let i = 0; i < orderIds.length; i += ID_CHUNK) {
        idChunks.push(orderIds.slice(i, i + ID_CHUNK))
      }
      const itemChunks = await runInBatches(idChunks, 4, (chunk) =>
        fetchAllPages<any>('productos', (from, to) =>
          supabase
            .from('order_items')
            .select('*')
            .in('order_id', chunk)
            .order('id', { ascending: true })
            .range(from, to),
        ),
      )
      const itemsRaw = itemChunks.flat()

      // Si llegó una respuesta más nueva mientras cargábamos, ésta se
      // descarta para no pisar datos con un filtro viejo.
      if (requestId !== requestIdRef.current) return

      // Lookup maps
      const orderToRest: Record<string, string> = {}
      validOrders.forEach((o: any) => {
        orderToRest[String(o.id)] = String(o.restaurant_id)
      })
      const restNameById = new Map<string, string>()
      const restSlugById = new Map<string, string>()
      allRestaurants.forEach((r) => {
        restNameById.set(String(r.id), r.name)
        restSlugById.set(String(r.id), r.slug || '')
      })

      // ─── 4. GMV totals ─────────────────────────────────────────
      const totalGmv = validOrders.reduce(
        (sum: number, o: any) => sum + Number(o.total || 0),
        0,
      )
      const totalOrders = validOrders.length
      const avgTicket = totalOrders > 0 ? totalGmv / totalOrders : 0

      // ─── 5. GMV daily + hourly (hora de Costa Rica) ────────────
      const gmvDailyMap: Record<
        string,
        { day: string; gmv: number; orders: number }
      > = {}
      const hourlyMap: Record<
        string,
        {
          hour: number
          restaurant_id: string
          restaurant_name: string
          gmv: number
          orders: number
        }
      > = {}
      validOrders.forEach((o: any) => {
        const cr = crDayHour(String(o.created_at))
        if (!cr) return
        const amount = Number(o.total || 0)
        if (!gmvDailyMap[cr.day]) {
          gmvDailyMap[cr.day] = { day: cr.day, gmv: 0, orders: 0 }
        }
        gmvDailyMap[cr.day].gmv += amount
        gmvDailyMap[cr.day].orders += 1
        const key = String(cr.hour)
        if (!hourlyMap[key]) {
          hourlyMap[key] = {
            hour: cr.hour,
            restaurant_id: '',
            restaurant_name: '',
            gmv: 0,
            orders: 0,
          }
        }
        hourlyMap[key].gmv += amount
        hourlyMap[key].orders += 1
      })
      const gmvDaily = Object.values(gmvDailyMap).sort((a, b) =>
        a.day.localeCompare(b.day),
      )
      const salesHourly = Object.values(hourlyMap).sort(
        (a, b) => Number(a.hour) - Number(b.hour),
      )
      const salesDaily = gmvDaily.map((d) => ({
        ...d,
        restaurant_id: '',
        restaurant_name: '',
      }))

      // ─── 6. GMV by restaurant ──────────────────────────────────
      const gmvByRestMap: Record<
        string,
        {
          restaurant_id: string
          restaurant_name: string
          slug: string
          gmv: number
          orders: number
        }
      > = {}
      validOrders.forEach((o: any) => {
        const rid = String(o.restaurant_id)
        if (!gmvByRestMap[rid]) {
          gmvByRestMap[rid] = {
            restaurant_id: rid,
            restaurant_name: restNameById.get(rid) || 'Sin nombre',
            slug: restSlugById.get(rid) || '',
            gmv: 0,
            orders: 0,
          }
        }
        gmvByRestMap[rid].gmv += Number(o.total || 0)
        gmvByRestMap[rid].orders += 1
      })
      const gmvByRestaurant = Object.values(gmvByRestMap)
        .map((r) => ({
          ...r,
          avg_ticket: r.orders > 0 ? r.gmv / r.orders : 0,
        }))
        .sort((a, b) => b.gmv - a.gmv)

      // ─── 7. Canales (por orders.source) ────────────────────────
      const channelCounts: Record<ChannelKey, number> = {
        web: 0,
        qr: 0,
        waiter: 0,
        pos_quick_sale: 0,
        unclassified: 0,
      }
      validOrders.forEach((o: any) => {
        channelCounts[classifySource(o.source)] += 1
      })
      const pctOf = (n: number) =>
        totalOrders > 0 ? (n / totalOrders) * 100 : 0
      const channelDistribution = (Object.keys(channelCounts) as ChannelKey[])
        .map((k) => ({
          channel: CHANNEL_LABELS[k],
          orders: channelCounts[k],
          pct: pctOf(channelCounts[k]),
        }))
        .filter((c) => c.orders > 0)
        .sort((a, b) => b.orders - a.orders)

      // ─── 8. Autoservicio = Marketplace + QR ────────────────────
      // Denominador: TODAS las órdenes válidas, incluidas las no
      // clasificadas. Caja se mantiene separada de Mesero.
      const selfServicePct: NoizaSelfServicePct = {
        self_service_pct: pctOf(channelCounts.web + channelCounts.qr),
        waiter_pct: pctOf(channelCounts.waiter),
        cashier_pct: pctOf(channelCounts.pos_quick_sale),
        unclassified_pct: pctOf(channelCounts.unclassified),
      }

      // ─── 9. Top products from order_items ──────────────────────
      const productMap: Record<
        string,
        {
          product_name: string
          restaurant_id: string
          restaurant_name: string
          quantity: number
          revenue: number
        }
      > = {}
      itemsRaw.forEach((item: any) => {
        const name = item.item_name_snapshot || 'Producto'
        const oid = String(item.order_id || '')
        const rid = orderToRest[oid] || ''
        const rname = restNameById.get(rid) || ''
        const qty = Number(item.quantity) || 1
        const lineTotal = Number(item.line_total)
        const unitPrice = Number(item.unit_price_snapshot || 0)
        const revenue =
          Number.isFinite(lineTotal) && lineTotal > 0
            ? lineTotal
            : qty * unitPrice
        const key = `${rid}__${name}`
        if (!productMap[key]) {
          productMap[key] = {
            product_name: name,
            restaurant_id: rid,
            restaurant_name: rname,
            quantity: 0,
            revenue: 0,
          }
        }
        productMap[key].quantity += qty
        productMap[key].revenue += revenue
      })
      const topProducts = Object.values(productMap).sort(
        (a, b) => b.revenue - a.revenue,
      )

      // ─── 10. Clientes únicos y recurrentes ─────────────────────
      const phoneCounts: Record<string, number> = {}
      validOrders.forEach((o: any) => {
        const k = o.customer_phone || o.customer_name
        if (!k) return
        const key = String(k).trim().toLowerCase()
        phoneCounts[key] = (phoneCounts[key] || 0) + 1
      })
      const totalDistinctCustomers = Object.keys(phoneCounts).length
      const repeatCount = Object.values(phoneCounts).filter((c) => c > 1).length
      const repeatCustomers: NoizaRepeatCustomers = {
        repeat_count: repeatCount,
        repeat_pct:
          totalDistinctCustomers > 0
            ? (repeatCount / totalDistinctCustomers) * 100
            : 0,
      }

      // ─── 11. Recent orders (más recientes primero) ─────────────
      const recentOrders = validOrders
        .slice(-50)
        .reverse()
        .map((o: any) => ({
          id: String(o.id),
          created_at: o.created_at,
          total: Number(o.total || 0),
          status: o.status || '',
          restaurant_id: String(o.restaurant_id),
          restaurant_name: restNameById.get(String(o.restaurant_id)) || '',
          customer_name: o.customer_name || 'Cliente',
        }))

      // ─── 12. Active restaurants = distinct restaurant_ids ──────
      const activeRestaurants = new Set(
        validOrders.map((o: any) => String(o.restaurant_id)),
      ).size

      // ─── 13. Total cobrado (payments.amount) ───────────────────
      const collectedMap: Record<string, NoizaCollectedByRestaurant> = {}
      let collectedTotal = 0
      paymentsRaw.forEach((p: any) => {
        const amount = Number(p.amount || 0)
        collectedTotal += amount
        const rid =
          p.restaurant_id === null || p.restaurant_id === undefined
            ? ''
            : String(p.restaurant_id)
        if (!collectedMap[rid]) {
          collectedMap[rid] = {
            restaurant_id: rid,
            restaurant_name: rid
              ? restNameById.get(rid) || 'Sin nombre'
              : 'Sin restaurante',
            amount: 0,
            payments: 0,
          }
        }
        collectedMap[rid].amount += amount
        collectedMap[rid].payments += 1
      })
      const collected: NoizaCollected = {
        total: collectedTotal,
        payments: paymentsRaw.length,
        byRestaurant: Object.values(collectedMap).sort(
          (a, b) => b.amount - a.amount,
        ),
      }

      setData({
        gmv: {
          total_gmv: totalGmv,
          total_orders: totalOrders,
          avg_ticket: avgTicket,
        },
        gmvDaily,
        gmvByRestaurant,
        channelDistribution,
        selfServicePct,
        activeRestaurants,
        uniqueCustomers: totalDistinctCustomers,
        repeatCustomers,
        topProducts,
        lowDays: [],
        lowHours: [],
        salesDaily,
        salesHourly,
        recentOrders,
        collected,
      })
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return
      console.error('[NOIZA Platform] Error fetching metrics:', err)
      // Nunca dejamos visible una suma parcial o vieja como si fuera el
      // total del filtro actual.
      setData(DEFAULT_DATA)
      setError(err?.message || 'Error loading platform metrics')
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [restaurantId, dateRange, allRestaurants])

  useEffect(() => {
    fetchAll()
    const interval = setInterval(fetchAll, 60000)
    return () => clearInterval(interval)
  }, [fetchAll])

  // Realtime subscription — refetch on any order change
  useEffect(() => {
    const filterById = restaurantId && restaurantId !== 'all'
    const channelName = `noiza-metrics-orders-${restaurantId || 'all'}`

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          ...(filterById ? { filter: `restaurant_id=eq.${restaurantId}` } : {}),
        },
        () => {
          console.log('[NOIZA Metrics] Order change detected — refetching')
          fetchAll()
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [restaurantId, fetchAll])

  return { data, loading, error, refetch: fetchAll, restaurants }
}
